import type { FieldCode } from "./failure";

/**
 * Shared kernel, the **web's** copy for each field code, and the single source
 * of truth for the sentences the screen docs quote.
 *
 * **Why it lives here rather than in Identity (moved in R22 T4).** It was
 * Identity's while Identity was the only context producing field codes. Catalog
 * now produces them too, and its add-practitioner form reuses the very same
 * name and phone codes, because they are the very same fields. A copy of those
 * six sentences in a second module would be six sentences that can disagree
 * with each other, which is the drift this map exists to prevent. The map is a
 * *copy catalogue*, not a domain rule, so the shared kernel is where it belongs
 * once more than one context reads it.
 *
 * `Record<FieldCode, string>` is exhaustive by type: adding a code to the union
 * and forgetting a sentence for it fails the build rather than rendering
 * `undefined` under somebody's input.
 */
export const FIELD_COPY: Record<FieldCode, string> = {
  // Identity
  first_name_required: "First name is required",
  first_name_too_long: "First name is too long",
  last_name_required: "Last name is required",
  last_name_too_long: "Last name is too long",
  phone_required: "Phone number is required",
  phone_invalid: "Enter a valid Lebanese phone number",
  email_invalid: "Enter a valid email address",
  password_required: "Password is required",
  password_too_short: "Password must be at least 8 characters",
  passwords_do_not_match: "Passwords do not match",
  current_password_required: "Enter your current password",
  // The number is written out rather than interpolated from Identity's
  // `OTP_CODE_LENGTH`, which the shared kernel may not import. A unit test in
  // Identity pins the two together, so a change to the code length fails there
  // instead of quietly leaving this sentence wrong.
  otp_code_invalid: "Enter the 6-digit code",
  member_phone_taken: "That number already has a business account.",

  // Catalog: Services
  service_name_required: "Service name is required",
  service_name_too_long: "Service name is too long",
  service_name_duplicate: "You already have a service with this name.",
  service_price_invalid: "Enter a price in USD (up to 2 decimals).",
  // H39: an appointment occupies exactly one hour, so a Service cannot be
  // longer than one.
  service_duration_invalid: "Enter a duration between 5 and 60 minutes.",

  // Catalog: the Business profile
  business_name_required: "Business name is required",
  business_name_too_long: "Business name is too long",
  website_invalid: "Enter a valid link (https://…)",
  location_pin_required: "Drop a pin on your clinic's location",
  categories_required: "Pick at least one category",
  home_surcharge_invalid:
    "Enter a home-visit surcharge in USD (up to 2 decimals).",
  // H59
  about_required: "Tell people about your clinic.",
  about_too_long: "Keep this under 1,000 characters.",
  social_instagram_invalid: "Enter an Instagram link (https://instagram.com/…).",
  social_facebook_invalid: "Enter a Facebook link (https://facebook.com/…).",
  social_tiktok_invalid: "Enter a TikTok link (https://tiktok.com/…).",
  social_linkedin_invalid: "Enter a LinkedIn link (https://linkedin.com/…).",
  social_x_invalid: "Enter an X link (https://x.com/…).",
  social_youtube_invalid: "Enter a YouTube link (https://youtube.com/…).",

  // Scheduling: the add-blackout form
  blackout_start_date_required: "Pick the first day away.",
  blackout_end_date_required: "Pick the first day back.",
  blackout_end_before_start:
    "The first day back must be after the first day away.",
  blackout_hours_required: "Pick the hours they are away.",
  blackout_end_hour_before_start: "The end hour must be after the start hour.",
  blackout_reason_too_long: "Keep the reason under 200 characters.",

  // Onboarding: the admin's rejection note
  reject_note_too_long: "Keep the note under 500 characters",

  // Customer Records: the note body. The length is written out rather than
  // interpolated from `NOTE_MAX`, which the shared kernel may not import; a
  // unit test in Customer Records pins the two together, exactly as one does
  // for the OTP length above.
  note_body_required: "Write something before saving.",
  note_body_too_long: "Keep a note under 4000 characters.",

  // Meal Plans: the plan and template editors. The limits are written out
  // rather than imported from the module, for the reason given above; a unit
  // test in Meal Plans pins them to the domain's constants.
  meal_plan_title_required: "Give the plan a title.",
  meal_plan_title_too_long: "Keep the title under 120 characters.",
  meal_plan_start_date_required: "Pick the day the plan starts.",
  meal_plan_end_date_invalid: "Pick a valid end date, or leave it empty.",
  meal_plan_end_before_start: "The plan cannot end before it starts.",
  meal_plan_guidance_too_long: "Keep the guidance under 2000 characters.",
  meal_plan_days_too_many: "A plan can have at most 14 days.",
  meal_plan_items_too_many:
    "A day can have at most 8 meals, and a meal at most 20 items.",
  meal_plan_entry_too_long:
    "Keep day names under 40 characters, items under 200 and quantities under 60.",
  meal_plan_macro_invalid: "Macros are grams, from 0 to 1000.",
};

/** Render a coded field map as the web's sentences. Adapters only. */
export function renderFieldErrors(
  fieldErrors: Readonly<Record<string, FieldCode>> | undefined,
): Record<string, string> {
  const rendered: Record<string, string> = {};
  for (const [field, code] of Object.entries(fieldErrors ?? {})) {
    rendered[field] = FIELD_COPY[code];
  }
  return rendered;
}
